// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Request error modal.
 * @author James Reid
 */

// @ts-check

// @@imports-package
import { App } from "../../app/index.js"
import { SteppedContainer } from "../../components/index.js"

// @@imports-utils
import { cloneTemplate } from "../document/index.js"

// @@body
/**
 * Render request error message as modal to user, including instructions on what
 * to do if issue persists, and information about the error.
 * @param {string} message Error message returned by server.
 * @param {number} statusCode Status code returned by server.
 * @returns {Promise<null>} Returns promise which resolves with no data after
 * error modal has been dismissed by the user.
 */
const alertRequestError = (message, statusCode) => {
    // Fetch required containers for rendering modal.
    const { container } = App.instance
    const modal = SteppedContainer.createContainer("modal")
    const requestError = cloneTemplate("request-error-modal")

    // Create and populate error message string.
    const dateString = `[${new Date()
        .toISOString()
        .replace("T", " ")
        .replace("Z", "")}]`
    const errorMessage = `${dateString} ${statusCode}: ${message}`;
    /** @type {HTMLParagraphElement} */ (requestError
        .querySelector("div.error-message p"))
        .innerText = errorMessage

    // Add copy to clipboard functionality to error message.
    requestError.querySelector("div.error-message button")
        ?.addEventListener("click", () => {
            navigator.clipboard.writeText(errorMessage)
        })

    // Append modal content to container and load.
    modal.append(requestError)
    container.loadModal(modal)

    return new Promise(resolve => {
        // Add modal close functionality to primary button, resolving returned
        // promise so that a request can be retried, or another appropriate
        // action can be taken.
        requestError.querySelector("button.primary")
            ?.addEventListener("click", async () => {
                await container.unloadModal()
                resolve(null)
            })
    })
}

// @@exports
export { alertRequestError }
